/**
 * Вспомогательные функции для установки значений AMO полей
 * Обеспечивают обновление UI и модели AMO
 */

import { findFieldContainer, dispatchEvents, getAmoAttributes, updateMasterCheckboxState } from './amoFieldsUtilsHelpers.js';
import { getCurrentAmoFieldValue } from './amoFieldsCore.js';

export function getAmoFieldById(fieldId) {
    try {
        const amoFields = AMOCRM.constant("account").cf || {};
        return amoFields[fieldId] || Object.values(amoFields).find(field => (
            String(field?.ID) === String(fieldId)
        )) || null;
    } catch (error) {
        console.warn(`Поле ${fieldId} не найдено в AMO:`, error);
        return null;
    }
}

/**
 * Обновление мастер-чекбоксов и заголовка мультиселекта
 */
export function updateMultiselectMasterElements(fieldContainer, checkedCount, totalCount) {
    try {
        const masterCheckboxes = fieldContainer.querySelectorAll('.js-master-checkbox');
        
        let state;
        if (checkedCount === 0) {
            state = 'none';
        } else if (checkedCount === totalCount) {
            state = 'all';
        } else {
            state = 'partial';
        }
        
        masterCheckboxes.forEach(masterCheckbox => {
            updateMasterCheckboxState(masterCheckbox, state);
        });
        
        updateMultiselectTitle(fieldContainer, checkedCount, totalCount);
        
    } catch (error) {
        console.warn('Ошибка обновления элементов мультиселекта:', error);
    }
}

/**
 * Обновление заголовка мультиселекта
 */
export function updateMultiselectTitle(fieldContainer, checkedCount, totalCount) {
    try {
        const titleWrapper = fieldContainer.querySelector('.checkboxes_dropdown__title-selected');
        if (!titleWrapper) return;
        
        const titleContainer = titleWrapper.querySelector('.checkboxes_dropdown__title');
        if (!titleContainer) return;
        
        titleContainer.innerHTML = '';
        
        if (checkedCount === 0) {
            const titleItem = document.createElement('div');
            titleItem.className = 'checkboxes_dropdown__title-item';
            titleItem.setAttribute('data-title-before', '');
            titleItem.textContent = 'Выбрать';
            titleContainer.appendChild(titleItem);
            
            const titleMasterWrapper = fieldContainer.querySelector('.checkboxes_dropdown__title_wrapper .js-master-checkbox-wrapper');
            if (titleMasterWrapper) {
                titleMasterWrapper.classList.remove('is-checked');
                const titleMasterCheckbox = titleMasterWrapper.querySelector('.js-master-checkbox');
                if (titleMasterCheckbox) {
                    titleMasterCheckbox.checked = false;
                    titleMasterCheckbox.removeAttribute('checked');
                }
                const titleHelper = titleMasterWrapper.querySelector('.control-checkbox__helper');
                if (titleHelper) {
                    titleHelper.classList.remove('control-checkbox__helper_minus');
                }
            }
        } else if (checkedCount === totalCount) {
            const titleItem = document.createElement('div');
            titleItem.className = 'checkboxes_dropdown__title-item';
            titleItem.setAttribute('data-title-before', '');
            titleItem.textContent = 'Все значения';
            titleContainer.appendChild(titleItem);
        } else {
            const checkedCheckboxes = fieldContainer.querySelectorAll('input.js-item-checkbox:checked');
            checkedCheckboxes.forEach((checkbox, index) => {
                const label = checkbox.closest('label.checkboxes_dropdown__label');
                const textElement = label?.querySelector('.checkboxes_dropdown__label_title');
                if (textElement) {
                    const titleItem = document.createElement('div');
                    titleItem.className = 'checkboxes_dropdown__title-item';
                    if (index === 0) {
                        titleItem.setAttribute('data-title-before', '');
                    }
                    titleItem.textContent = textElement.textContent.trim();
                    titleContainer.appendChild(titleItem);
                }
            });
        }
        
        console.log(`Заголовок мультиселекта обновлен: ${checkedCount}/${totalCount}`);
        
    } catch (error) {
        console.warn('Ошибка обновления заголовка мультиселекта:', error);
    }
}

/**
 * Обновление модели AMO и сохранение
 */
export function updateAmoModel(fieldId, value) {
    try {
        const { attributes, cfvKey, success } = getAmoAttributes(fieldId);
        if (!success) {
            return false;
        }
        
        if (Array.isArray(value)) {
            const field = getAmoFieldById(fieldId);
            const normalizedValue = value.map(String);
            
            if (field && field.ENUMS) {
                console.log(`Обновление мультиселекта ${fieldId}, новые значения:`, value);
                
                Object.keys(field.ENUMS).forEach(enumId => {
                    const enumKey = `${cfvKey}[${enumId}]`;
                    
                    if (normalizedValue.includes(String(enumId))) {
                        attributes[enumKey] = enumId;
                        console.log(`Выбран: ${enumKey} = ${enumId}`);
                    } else {
                        attributes[enumKey] = "";
                        console.log(`Снят: ${enumKey} = ""`);
                    }
                });
                
                attributes[cfvKey] = value;
            } else {
                console.warn(`Поле ${fieldId} не найдено в AMO или не имеет ENUMS`);
                attributes[cfvKey] = value;
            }
        } else {
            attributes[cfvKey] = value;
        }
        
        try {
            AMOCRM.data.current_card.model.set(cfvKey, value, { silent: true });
        } catch (setError) {
            console.warn('Ошибка model.set, продолжаем без него:', setError);
        }
        
        if (AMOCRM.data.current_card.model.changed) {
            AMOCRM.data.current_card.model.changed[cfvKey] = value;
        }
        
        setTimeout(() => {
            try {
                const { element: fieldContainer } = findFieldContainer(fieldId, { silent: true });
                if (fieldContainer) {
                    dispatchEvents(fieldContainer, 'amoFieldUpdated', {
                        detail: { fieldId, value }
                    });
                }
                
                if (window.dispatchEvent) {
                    dispatchEvents(window, 'amoModelUpdated', {
                        detail: { fieldId, value }
                    });
                }
                
                forceUpdateAmoFieldsValidation();
            } catch (e) {
                console.warn('Ошибка принудительного обновления UI:', e);
            }
        }, 100);
        
        if (AMOCRM.data.current_card?.save) {
            AMOCRM.data.current_card.save();
            console.log('Карточка AMO сохранена');
        }

        return true;
    } catch (error) {
        console.error('Ошибка обновления модели AMO:', error);
        return false;
    }
}

/**
 * Принудительное обновление валидации всех AMO полей
 */
export function forceUpdateAmoFieldsValidation() {
    try {
        const amoFields = document.querySelectorAll('[data-id]');
        
        amoFields.forEach(fieldContainer => {
            const fieldId = fieldContainer.getAttribute('data-id');
            if (!fieldId) return;
            
            try {
                const fieldValue = getCurrentAmoFieldValue(fieldId);
                
                dispatchEvents(fieldContainer, 'amoFieldValidationUpdate', {
                    detail: { fieldId, value: fieldValue, hasValue: Boolean(fieldValue) }
                });
                
            } catch (e) {
                console.warn(`Ошибка обновления валидации поля ${fieldId}:`, e);
            }
        });
        
        if (window.dispatchEvent) {
            dispatchEvents(window, 'amoFieldsValidationUpdated');
        }
        
        console.log('Валидация AMO полей обновлена');
    } catch (error) {
        console.error('Ошибка принудительного обновления валидации:', error);
    }
}
